import Column from '@components/Column';
import CardSide from '@components/membership/CardSide';
import Row from '@components/Row';
import ScrollBar from '@components/ScrollBar';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { PointCardRow } from '@models/point';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import usePointHistory from './hooks/usePointHistory';
import PointLogRow from './PointLogRow';

interface Props {
    pointCard: PointCardRow;
}

function PointLog({ pointCard }:Props) {
    const { t } = useTranslation('point');
    const { shopName, balance, shopId } = pointCard;
    const [expand, setExpand] = useState<boolean>(false);
    const { pointHistories, fetchNext } = usePointHistory(shopId);
    const ref = useRef<HTMLDivElement>(null);
    return (
        <Container>
            <Top>
                <Bar isExpanded={expand} />
                <Side color={expand ? colors.gray6 : colors.gray4}/>

                <ShopNameText fontWeight='bold'
                    color={colors.gray9} >
                    {shopName}
                </ShopNameText>
            </Top>

            <Base isExpanded={expand} >
                <Contents isCollapsed={!expand}>
                    <DashedDivider/>
                    <LogsContainer>
                        <Logs fullWidth spacing={18}
                            onScroll={(e) => {
                                const { scrollTop, scrollHeight, offsetHeight } = e.currentTarget;
                                const percent = scrollTop / (scrollHeight - offsetHeight);
                                if (percent >= 0.9) {
                                    fetchNext();
                                }
                            }}
                            ref={ref} >
                            {pointHistories.map((pointHistory, i) => (
                                <PointLogRow pointHistory={pointHistory} key={i.toString()} />
                            ))}
                        </Logs>
                        <ScrollBar containerRef={ref}
                            containerHeight={386}/>
                    </LogsContainer>
                </Contents>
            </Base>
            <Overlay isExpanded={expand} />
            <TextWrapper onClick={() => setExpand(!expand)}>
                <Text size='lg' fontWeight='bold' color={expand ? colors.white : colors.gray7}>
                    {balance.toLocaleString()}{t('card.pointUnit')}
                </Text>
            </TextWrapper>
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: relative;
    width: 100%;
    background-color: white;
`;

const Top = styled(Row)`
    margin-bottom: -1px;
`;

const Bar = styled.div<{
    isExpanded: boolean;
}>`
    background-color: var(--color-gray4);
    border-top-left-radius: 12px;
    flex: 1;
    height: 17px;

    ${({ isExpanded }) => (isExpanded ? css`
        background-color: var(--color-gray6);
    ` : undefined)}
`;

const Side = styled(CardSide)`
    margin-bottom: -1px;
    margin-left: -2px;
`;

const ShopNameText = styled(Text)`
    margin-right: 8px;
    margin-top: -9px;
    z-index: 3;
`;

const Base = styled.div<{
    isExpanded: boolean;
}>`
    background-color: var(--color-gray4);
    border-radius: 0 12px 12px 12px;
    min-height: 36px;
    width: 100%;
    padding: 0  0 0 18px;
    overflow: hidden;

    ${({ isExpanded }) => (isExpanded ? css`
        background-color: var(--color-gray6);
    ` : undefined)}
`;

const Overlay = styled.div<{
    isExpanded: boolean;
}>`
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    mix-blend-mode: overlay;
    pointer-events: none;
    position: absolute;
    z-index: 2;
    background: linear-gradient(180deg, var(--color-gray5) 33.17%, var(--color-gray3) 100%);

    ${({ isExpanded }) => (isExpanded ? css`
        background: linear-gradient(180deg, var(--color-gray7) 0%, var(--color-gray6) 88.94%);
    ` : undefined)}
`;

const TextWrapper = styled.div`
    padding: 14px 18px;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 3;
`;

const DashedDivider = styled.div`
    border-top: 2px dashed var(--color-white);
    margin-bottom: 20px;
    margin-top: 36px;
    width: calc(100% - 22px);
`;

const Logs = styled(Column)`
    max-height: 386px;
    overflow-y: auto;
    padding-right: 36px;
`;

const LogsContainer = styled.div`
    position: relative;
`;

const Contents = styled.div<{
    isCollapsed: boolean;
}>`
    max-height: 500px;
    overflow: hidden;
    transition: max-height 350ms;

    ${({ isCollapsed }) => (isCollapsed ? css`
        max-height: 0;
    ` : undefined)}
`;

export default PointLog;
